// components/upload/UploadedAsset.jsx
import React, { useRef } from "react";
import { MdDelete, MdEdit } from "react-icons/md";
import { getVideoType } from "../utils/getVideoType";
import VideoThumbnail from "../../tipsAndDrills/VideoThumbnail";

/**
 * Polymorphic preview for image/video with edit & delete controls.
 *
 * Props:
 * - type: "image" | "video"
 * - name: string
 * - src: string
 * - reuploadId: string
 * - uploadFn: (e) => void
 * - deleteFn?: (name) => void
 * - onMetadata?: (name, durationSeconds) => void  // used for video
 */
export function UploadedAsset({
  type,
  name,
  src,
  reuploadId,
  uploadFn,
  deleteFn,
  onMetadata,
}) {
  const videoRef = useRef(null);

  const handleLoadedMetadata = () => {
    if (!onMetadata) return;
    try {
      if (videoRef.current) {
        const dur = Math.floor(videoRef.current.duration || 0);
        onMetadata(name, dur);
      }
    } catch {
      // no-op
    }
  };

  return (
    <div className="w-full relative rounded-md overflow-hidden">
      {type === "image" ? (
        <img
          className="w-full object-cover h-auto block"
          src={src}
          alt={name}
        />
      ) : (
        <VideoThumbnail
          src={src}
          type={getVideoType(src)}
          className="bg-black w-full max-w-full block object-cover"
        />
      )}

      {/* Edit (reupload) */}
      <label
        htmlFor={reuploadId}
        className="absolute bottom-2 right-2 size-8 bg-white rounded-full shadow flex items-center justify-center cursor-pointer"
        title="Reupload"
      >
        <MdEdit className="text-gray800 text-lg" />
      </label>

      {/* Delete */}
      {deleteFn && (
        <button
          type="button"
          onClick={() => deleteFn(name)}
          className="absolute bottom-2 right-12 size-8 bg-white rounded-full shadow flex items-center justify-center"
          title="Delete"
        >
          <MdDelete className="text-gray800 text-lg" />
        </button>
      )}

      {/* Hidden input for reupload */}
      <input
        id={reuploadId}
        name={name}
        type="file"
        accept={type === "image" ? "image/*" : "video/*"}
        onChange={uploadFn}
        className="hidden"
      />
    </div>
  );
}
